import { Link } from "react-router";
import { t } from "@wr/i18n";
import { renderTemplate } from "../lib/rich";
import { LEGAL_FROM_APP } from "../routes/LegalScreen";

/**
 * An inline link into `/terms` or `/privacy`.
 *
 * **`target="_blank"` is LOCKED** (Ivy §22.5), and the load-bearing reason is
 * stronger than the one originally written down here: a half-filled sign-up form
 * must survive somebody checking what they are agreeing to — true — but the
 * drawer is reachable **during a game**, and a same-tab navigation to `/terms`
 * would unmount the game screen and drop a live room the player has coins in. A
 * legal document must never be able to cost someone a match.
 *
 * **The new tab is why the link declares itself.** `?from=app` tells the
 * document which side of the door the reader came in on, and the exit control on
 * that screen then offers the one honest way back: close the tab we opened. It is a
 * *declaration*, not a detection — see `LEGAL_FROM_APP`, where the two sniffing
 * approaches and why neither works are recorded.
 *
 * The scope of the new-tab rule is exactly this component: an inline link **out
 * of a stateful surface into a document**. Links inside a document body stay
 * same-tab, and the document's own exit controls never open a tab in any mode.
 */
export function LegalLink({ to, label, testId }: { to: string; label: string; testId?: string }) {
  return (
    <Link
      className="wr-link"
      to={`${to}?${LEGAL_FROM_APP}`}
      target="_blank"
      rel="noreferrer"
      {...(testId ? { "data-testid": testId } : {})}
    >
      {label}
    </Link>
  );
}

/**
 * **The 13+ attestation** — ToS §1 and Privacy Policy §8 both set the floor at
 * 13, and until T-234 nothing in the product asked.
 *
 * It is a **by-continuing sentence, not a checkbox**, because that is the
 * acceptance model the ToS's own preamble describes ("By creating an account,
 * playing as a guest, or otherwise using the Service, you agree to these
 * Terms"). It is an **attestation, not verification**: nothing here checks an
 * age, and nothing should pretend to.
 *
 * ── Where it renders, and why that is every account-creating action ─────────
 *
 * 1. Under the auth card, on **both** tabs — which is Ivy §6's layout drawing
 *    (`[ legal line, body-xs ]` sits under the card in both modes; the
 *    login-only render this replaces was T-170's reading of a register that had
 *    no slot for it). Both tabs matters: `Continue as guest` is on both, and a
 *    guest account is an account the ToS preamble binds.
 * 2. Under the conversion sheet's submit — the other place an account is
 *    created (§14).
 *
 * ── Accepted redundancy, flagged for Ivy and Theo (T-234 output) ────────────
 *
 * On the sign-up tab and in the sheet this sits below §6's LOCKED `auth.tos`
 * checkbox, so "agree to the Terms and Privacy Policy" is stated twice: once as
 * a ticked control, once in this sentence. The alternative was to fold the age
 * clause into the checkbox label — which rewrites ratified copy on a legal
 * control, and is Theo's and Ivy's call, not this ticket's. Belt and braces was
 * the safe direction to be wrong in. **Quinn: deliberate, not a duplicate.**
 */
export function LegalAttestation({ testId }: { testId?: string }) {
  return (
    <p
      className="wr-body-xs wr-ink-70"
      style={{ textAlign: "center" }}
      {...(testId ? { "data-testid": testId } : {})}
    >
      {renderTemplate(t("auth.legalAttestation"), {
        tosLink: <LegalLink to="/terms" label={t("legal.terms.title")} />,
        privacyLink: <LegalLink to="/privacy" label={t("legal.privacy.title")} />,
      })}
    </p>
  );
}
